import { Stack, Typography } from '@mui/material';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import { Dayjs } from 'dayjs';
import { Control, Controller, UseFormGetValues, UseFormTrigger } from 'react-hook-form';
import { getBrowser12HourTime } from 'v2/utils/dateTime';
import {
  EditScheduleFormValues,
  TIME_PICKER_WIDTH,
  TimeFieldName,
  toTimeValue
} from '../Details/components/editScheduleUtils';

interface Props {
  control: Control<EditScheduleFormValues>;
  fromField: TimeFieldName;
  toField: TimeFieldName;
  getValues: UseFormGetValues<EditScheduleFormValues>;
  trigger: UseFormTrigger<EditScheduleFormValues>;
  disabled?: boolean;
  validationMessage: string;
  requiredMessage: string;
}

const TimeRangeFields = ({
  control,
  fromField,
  toField,
  getValues,
  trigger,
  disabled = false,
  validationMessage,
  requiredMessage
}: Props) => {
  const handleChange = async (value: Dayjs | null, onChange: (value: string) => void) => {
    onChange(value?.format('HH:mm') ?? '');
    await trigger([fromField, toField]);
  };

  return (
    <Stack direction="row" alignItems="flex-start" gap={1}>
      <Controller
        name={fromField}
        control={control}
        rules={{
          validate: (value) => {
            if (disabled) return true;
            if (!value) return requiredMessage;
            return !getValues(toField) || value < getValues(toField) || validationMessage;
          }
        }}
        render={({ field, fieldState }) => (
          <DesktopTimePicker
            ampm={getBrowser12HourTime()}
            value={toTimeValue(field.value)}
            disabled={disabled}
            onChange={(value) => handleChange(value, field.onChange)}
            slotProps={{
              textField: {
                id: `${fromField.replace('.', '-')}-input`,
                size: 'small',
                error: !!fieldState.error,
                helperText: fieldState.error?.message,
                onBlur: field.onBlur,
                sx: { width: TIME_PICKER_WIDTH }
              }
            }}
          />
        )}
      />

      <Typography variant="body2" sx={{ mt: 1.25 }}>
        –
      </Typography>

      <Controller
        name={toField}
        control={control}
        rules={{
          validate: (value) => {
            if (disabled) return true;
            if (!value) return requiredMessage;
            return !getValues(fromField) || value > getValues(fromField) || validationMessage;
          }
        }}
        render={({ field, fieldState }) => (
          <DesktopTimePicker
            ampm={getBrowser12HourTime()}
            value={toTimeValue(field.value)}
            disabled={disabled}
            onChange={(value) => handleChange(value, field.onChange)}
            slotProps={{
              textField: {
                id: `${toField.replace('.', '-')}-input`,
                size: 'small',
                error: !!fieldState.error,
                helperText: fieldState.error?.message,
                onBlur: field.onBlur,
                sx: { width: TIME_PICKER_WIDTH }
              }
            }}
          />
        )}
      />
    </Stack>
  );
};

export default TimeRangeFields;
